/**
 * Privacy Utilities
 *
 * Small helpers for reducing exposure of Personally Identifiable Information (PII).
 * These helpers support privacy controls, but compliance also depends on consent,
 * processor terms, retention, deletion, and the surrounding data flow.
 */

import * as Crypto from 'expo-crypto';

/**
 * Hashes an email address using SHA-256 algorithm.
 *
 * This function is used when a flow needs a stable pseudonymous email identifier
 * instead of the raw email address, for example when attaching limited user context
 * to diagnostics.
 *
 * @param {string} email - The email address to hash
 * @returns {Promise<string|null>} A 64-character hex string representing the SHA-256 hash,
 *                                  or null if the email is empty/undefined
 *
 * @example
 * const hash = await hashEmail('user@example.com');
 * logger.log(hash); // '973dfe463ec85785f5f95af5ba3906eedb2d931c24e69824a89ea65dba4e813b'
 *
 * @example
 * const nullHash = await hashEmail('');
 * logger.log(nullHash); // null
 */
export async function hashEmail(email) {
  // Return null for empty, undefined, or null emails
  if (!email || email.trim() === '') {
    return null;
  }

  // Normalize email to lowercase before hashing
  // This ensures consistent hashes for the same email address
  const normalizedEmail = email.toLowerCase().trim();

  // Hash using SHA-256 algorithm
  const hash = await Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, normalizedEmail);

  // Return the 64-character hex hash
  return hash;
}
